export interface CareersPageContent {
  hero: {
    backgroundImage: string
    title: string
    subtitle: string
  }
  mainContent: {
    title: string
    subtitle: string
    paragraphs: string[]
  }
  benefits: {
    title: string
    subtitle: string
    paragraphs: string[]
  }
  reasonsToWork: {
    icon: React.ReactNode
    title: string
    description: string
  }[]
}

const content: CareersPageContent = {
  hero: {
    backgroundImage: '/images/shipsticks/hero-background.jpg',
    title: 'Career Opportunities',
    subtitle: "Join the team that's changing the way the world travels.",
  },
  mainContent: {
    title: 'Why choose ShipSticks?',
    subtitle: 'We travel light, but think big.',
    paragraphs: [
      'At ShipSticks, we believe getting to the golf course should be just as easy ' +
        "as being there. That's why we've made it our mission to simplify how golfers get " +
        'their gear to and from home - no stress, no baggage claim.',
      'Based in Palm Beach Gardens, Florida, our team is made up of problem-solvers, innovators, ' +
        'and big thinkers. We value collaboration, curiosity, and creative solutions that make ' +
        'travel easier - and catching the back nine of a major in the breakroom, of course.',
      "Explore our current openings below. Don't see the perfect fit? We're always looking for " +
        'great people to join our team. Send your resume to careers@shipsticks.com.',
    ],
  },
  benefits: {
    title: 'We invest in you.',
    subtitle: 'Because great work deserves great benefits.',
    paragraphs: [
      'Along with a competitive salary, health coverage (including dental and vision), ' +
        '401(k) matching, and paid time off, ShipSticks offers parental leave, flexible ' +
        'work options (including hybrid and remote), fun team outings, and employee ' +
        'discounts so you, too, can travel easier.',
    ],
  },
  reasonsToWork: [
    {
      icon: (
        <svg className="size-8" fill="currentColor" viewBox="0 0 24 24">
          {/* eslint-disable-next-line max-len */}
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z" />
        </svg>
      ),
      title: 'Competitive salary',
      description:
        'We value our people and offer comprehensive salary and benefits packages ' +
        'that reflect the importance of your role in our mission.',
    },
    {
      icon: (
        <svg className="size-8" fill="currentColor" viewBox="0 0 24 24">
          {/* eslint-disable-next-line max-len */}
          <path d="M13.13 22.19l-1.63-3.83c1.57-.58 3.04-1.36 4.4-2.27l-2.77 6.1zM5.64 12.5l-3.83-1.63 6.1-2.77c-.91 1.36-1.69 2.83-2.27 4.4zM12.5 18.36l1.63 3.83 2.77-6.1c-1.36.91-2.83 1.69-4.4 2.27zM18.36 11.5l3.83 1.63-6.1 2.77c.91-1.36 1.69-2.83 2.27-4.4zM12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" />
        </svg>
      ),
      title: 'Supersonic Growth',
      description:
        'Join a company with 100% organic growth, shipping over 100,000 bags annually and' +
        ' partnering with 3,000+ facilities worldwide.',
    },
    {
      icon: (
        <svg className="size-8" fill="currentColor" viewBox="0 0 24 24">
          {/* eslint-disable-next-line max-len */}
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
        </svg>
      ),
      title: 'Change the world',
      description:
        'We revolutionized the golf industry by offering an alternative to lugging golf ' +
        "clubs. We've shipped bags over 277 million miles.",
    },
    {
      icon: (
        <svg className="size-8" fill="currentColor" viewBox="0 0 24 24">
          {/* eslint-disable-next-line max-len */}
          <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zm4 18v-6h2.5l-2.54-7.63A1.5 1.5 0 0 0 18.54 8H17c-.8 0-1.54.37-2.01 1l-4.7 6.3c-.37.5-.58 1.11-.58 1.73V20c0 1.1.9 2 2 2h6c1.1 0 2-.9 2-2zM12.5 11.5c.83 0 1.5-.67 1.5-1.5s-.67-1.5-1.5-1.5S11 9.17 11 10s.67 1.5 1.5 1.5z" />
        </svg>
      ),
      title: 'Great environment',
      description:
        'Located in West Palm Beach, Florida, near beaches and outdoor activities. ' +
        'Enjoy a casual, team-like work atmosphere.',
    },
  ],
}

export default content
